import type { ChainIdentifier } from "@opensea/graphql"
import type { FlowStateVariants } from "../flow-state"

/**
 * Extracts chain identifier from a flow state.
 *
 * Chain information can be stored in various ways depending on the flow type:
 * - Direct `chain` field (e.g., CancelAllOrdersFlowState)
 * - From `items` array (e.g., CheckoutFlowState, AcceptOfferFlowState)
 * - From `currency` field (e.g., SendFlowState)
 * - From `fromCurrency`/`toCurrency` fields (e.g., SwapFlowState)
 * - From `tokenReward.balance.currency` (e.g., TokenClaimFlowState)
 * - Not available (e.g., ConnectFlowState, ViewWalletFlowState)
 *
 * @param flow - The flow state to extract chain from
 * @returns The chain identifier if available, undefined otherwise
 */
export function extractChainFromFlowState(
  flow: FlowStateVariants,
): ChainIdentifier | undefined {
  switch (flow.type) {
    case "cancel-all-orders":
      return flow.chain

    case "checkout":
    case "accept-offer":
    case "create-offer":
    case "listing":
    case "transfer":
      // Extract chain from first item
      return flow.items[0]?.chain?.identifier

    case "send":
      // Extract chain from currency
      return flow.currency?.chainIdentifier

    case "swap":
      // Prefer fromCurrency, fallback to toCurrency
      return (
        flow.fromCurrency?.chainIdentifier ?? flow.toCurrency?.chainIdentifier
      )

    case "token-claim":
      // Extract chain from token reward balance currency
      return flow.tokenReward.balance?.currency?.chainIdentifier

    case "collection-sweep":
    case "create-collection-offer":
    case "view-wallet":
    case "connect":
    case "siwe":
    case "deposit":
    case "topup":
    case "fiat-checkout":
    case "mint":
    case "accept-terms":
    case "authenticate":
    case "enforce-creator-fees":
    case "unenforce-creator-fees":
    case "set-royalty-info":
    case "drop-publish":
    case "cancel-order":
      // These flows don't have chain information directly available
      return undefined

    default: {
      // Exhaustiveness check - TypeScript will error if we miss a case
      const _exhaustive: never = flow
      return undefined
    }
  }
}
